import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { API_ROUTES, QUERY_KEYS } from '@/config/constants';
import { api } from '@/lib/axios';
import { resolveApiAssetUrl } from '@/utils/asset-url';
import type { QueryConfig } from '@/lib/react-query';
import type { WholesalerOverview } from '@/types/auth';

// `logo` comes back as an API-relative upload path — normalised here so no consumer has to
// know where the backend serves its files from.
export const fetchOverview = async (wholesalerId: string): Promise<WholesalerOverview> => {
  const { data } = await api.get<WholesalerOverview>(API_ROUTES.overview, {
    params: { wholesalerId },
  });
  return { ...data, logo: resolveApiAssetUrl(data.logo) };
};

const WORKSPACE_LOGO_STALE_MS = 5 * 60 * 1000;

export const getOverviewQueryOptions = (wholesalerId: string) =>
  queryOptions({
    queryKey: QUERY_KEYS.overview.byWholesaler(wholesalerId),
    queryFn: () => fetchOverview(wholesalerId),
    staleTime: 0,
    refetchOnMount: 'always' as const,
  });

export const useOverview = (
  wholesalerId: string | null,
  queryConfig?: QueryConfig<typeof getOverviewQueryOptions>,
) =>
  useQuery({
    ...getOverviewQueryOptions(wholesalerId ?? 'none'),
    ...queryConfig,
    // `enabled` stays last so a caller's own options can never re-enable the query for a
    // missing wholesaler id.
    enabled: wholesalerId != null,
  });

/**
 * Logos for a set of workspaces, keyed by wholesaler id.
 *
 * GET /me/workspaces carries no logo, and /overview is the only endpoint that exposes one — so
 * resolving a logo per row costs one request per workspace. It accepts any wholesaler the caller
 * is a member of, and shares its cache with the single-workspace hook above, so the workspace in
 * scope is already paid for. Gated behind `enabled` (pass the switcher's open state) so the
 * fan-out only happens when the rows are actually on screen.
 */
export const useWorkspaceLogos = (
  wholesalerIds: string[],
  enabled: boolean,
): Record<string, string | null> =>
  useQueries({
    queries: wholesalerIds.map((wholesalerId) => ({
      ...getOverviewQueryOptions(wholesalerId),
      staleTime: WORKSPACE_LOGO_STALE_MS,
      refetchOnMount: false as const,
      enabled,
    })),
    combine: (results) => {
      const logos: Record<string, string | null> = {};
      wholesalerIds.forEach((wholesalerId, index) => {
        logos[wholesalerId] = results[index]?.data?.logo ?? null;
      });
      return logos;
    },
  });
